import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';

import { stripAppRoot } from 'history/AppRootUtils';
import { isMaybeAuthenticated } from 'helpers/AuthHelpers';
import { buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import { getWriteReviewReturnUrl } from 'helpers/ReviewUtils';
import {
  checkCustomerAuthentication, fetchCustomerAuthDetails
} from 'actions/authentication';
import { redirectToAuthenticationFor } from 'actions/redirect';
import {
  addFieldValidationErrors, loadWriteReviewPage, resetAddReview,
  updateAbout, updateFit, updateRating, updateSummary
} from 'actions/reviews';
import { addReview as addCloudReview } from 'actions/addReview';
import { PageLoader } from 'components/Loader';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import AddReviewForm from 'components/reviews/AddReviewForm';
import AddReviewConfirmation from 'components/reviews/AddReviewConfirmation';
import css from 'styles/containers/addReview.scss';

export class AddReview extends Component {

  static fetchDataOnServer(store, location, { productId }) {
    if (isMaybeAuthenticated(store)) {
      return Promise.all([
        store.dispatch(fetchCustomerAuthDetails()),
        store.dispatch(loadWriteReviewPage(productId, { errorOnOos: false }))
      ]).then(() => {
        const { product: { authObj } } = store.getState();
        if (!authObj || !authObj.success) {
          store.dispatch(redirectToAuthenticationFor(location));
        }
      });
    } else {
      return Promise.resolve(store.dispatch(redirectToAuthenticationFor(location)));
    }
  }

  componentDidMount() {
    const {
      params: { productId, colorId, p: isPremier },
      product: { detail },
      checkCustomerAuthentication, loadWriteReviewPage, location
    } = this.props;

    const productChanged = (!detail || (detail.productId !== productId));
    const productIdUrl = getWriteReviewReturnUrl(productId, colorId);
    if (productChanged) {
      loadWriteReviewPage(productId, { errorOnOos: false });
    }

    // if we client routed here, check if they're logged in
    checkCustomerAuthentication(`${location.pathname}${location.search}${location.hash}`, () => null);
    // If we came in via the /review/asin/:asin route, replace this to the correct URL
    if (!productChanged && !isPremier && stripAppRoot(window.location.pathname) !== productIdUrl) {
      this.context.router.replacePreserveAppRoot(productIdUrl);
    }
  }

  componentDidUpdate(prevProps) {
    const { addReview: { isSubmitted: prevIsSubmitted }, loadWriteReviewPage, params: { productId: prevProductId } } = prevProps;
    const { addReview: { isSubmitted }, params: { productId } } = this.props;
    const productIdChanged = (productId !== prevProductId);

    if (productIdChanged) {
      loadWriteReviewPage(productId, { errorOnOos: false });
    }

    if (prevIsSubmitted !== isSubmitted && isSubmitted) {
      window.scrollTo(0, 0);
    }
  }

  componentWillUnmount() {
    const { resetAddReview } = this.props;
    resetAddReview();
  }

  makeSubmitReviewErrorMessage = () => {
    const { submitReviewErrorMessage } = this.props.addReview;
    return <div className={css.formError}>{submitReviewErrorMessage}</div>;
  }

  render() {
    const {
      props: {
        addReview: {
          isSubmitted, submitReviewErrorMessage
        },
        params: { productId, colorId, p: isPremier },
        product: { detail, isLoading: isLoadingProduct },
        addCloudReview, addReview, params, hasPassedAuthCheck, updateSummary,
        updateRating, updateFit, updateAbout,
        addFieldValidationErrors
      },
      makeSubmitReviewErrorMessage
    } = this;

    if (!detail || !hasPassedAuthCheck || isLoadingProduct) {
      return <PageLoader/>;
    } else if (isSubmitted && !submitReviewErrorMessage) {
      return (
        <SiteAwareMetadata>
          <AddReviewConfirmation/>
        </SiteAwareMetadata>
      );
    } else {
      const { brandName, productName } = detail;

      return (
        <SiteAwareMetadata>
          <div className={css.wrap}>
            <h1 className={css.heading}>
              Write a Review <Link to={buildSeoProductUrl(detail, colorId)}>{brandName} {productName}</Link>
            </h1>
            {submitReviewErrorMessage && makeSubmitReviewErrorMessage()}
            <AddReviewForm
              addReview={addReview}
              product={detail}
              params={params}
              returnTo={getWriteReviewReturnUrl(productId, colorId, isPremier)}
              onUpdateSummary={updateSummary}
              onUpdateRating={updateRating}
              onUpdateFit={updateFit}
              onUpdateAbout={updateAbout}
              onSubmitProductReview={addCloudReview}
              onAddFieldValidationErrors={addFieldValidationErrors}/>
          </div>
        </SiteAwareMetadata>
      );
    }
  }
}

AddReview.contextTypes = {
  router: PropTypes.object.isRequired
};

export function mapStateToProps(state) {
  const { authentication, product, reviews: { addReview } } = state;
  return {
    product,
    addReview,
    hasPassedAuthCheck: !!authentication
  };
}

const mapDispatchToProps = {
  addCloudReview,
  addFieldValidationErrors,
  checkCustomerAuthentication,
  loadWriteReviewPage,
  resetAddReview,
  updateSummary,
  updateRating,
  updateFit,
  updateAbout
};

export default connect(mapStateToProps, mapDispatchToProps)(AddReview);
